import {
  type AvatarPngSize,
  decodeAvatarSpec,
  DEFAULT_AVATAR_PNG_SIZE,
  encodeAvatarSpec
} from '../domains/avatar';

/**
 * Builds the URL of a generated voxel avatar from a stored spec (the value of
 * the `robohash` column — see `domains/avatar.ts` for why it still has that
 * name).
 *
 * The spec's own `?` becomes the URL's query string rather than being escaped
 * into the path, so the endpoint reads the choices with `req.query` and the URL
 * stays legible in a network tab:
 *
 *   `9f3c…?top=afro`  →  `https://api.factiii.com/avatar/9f3c….svg?top=afro`
 *
 * The path segment needs no escaping: `isValidAvatarSeed` already restricts a
 * seed to word characters, `.`, `:`, and `-`.
 *
 * Every distinct spec is a distinct URL, and the endpoint answers `immutable`,
 * so a changed choice retires its predecessor in every browser and image cache
 * on its own. Nothing has to be purged.
 *
 * Two formats, for consumers that differ. SVG is the default and the better
 * answer wherever it works: it carries no size, so one response serves the 24px
 * avatar in a comment header and the 160px one on a profile. PNG is for the
 * places that cannot take a vector at all —
 *
 *   - React Native's `Image`, which decodes PNG/JPEG/GIF/WebP and not SVG;
 *   - satori, which renders the Open Graph cards;
 *   - the OS notification icons the desktop app hands to Electron.
 */
export const buildAvatarUrl = (
  apiUrl: string,
  spec: string,
  raster?: { readonly format: 'png'; readonly size?: AvatarPngSize }
): string => {
  const { seed, options } = decodeAvatarSpec(spec);
  const encoded = encodeAvatarSpec(seed, options);
  const separator = encoded.indexOf('?');

  const query = new URLSearchParams(
    separator === -1 ? '' : encoded.slice(separator + 1)
  );
  if (raster) {
    query.set('size', String(raster.size ?? DEFAULT_AVATAR_PNG_SIZE));
  }

  const base = `${apiUrl.replace(/\/$/, '')}/avatar/${seed}.${
    raster ? 'png' : 'svg'
  }`;
  const search = query.toString();
  return search ? `${base}?${search}` : base;
};
